import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, TrendingUp, Clock, AlertTriangle } from 'lucide-react';

interface ActivityMetric {
  timestamp: string;
  steps: number;
  activeMinutes: number;
  gaitSpeed: number;
  gaitStability: number;
}

const ActivityTracking: React.FC = () => {
  // Mock data - in real app this would come from floor sensors
  const activityData: ActivityMetric[] = [
    { timestamp: '6AM', steps: 0, activeMinutes: 0, gaitSpeed: 0, gaitStability: 85 },
    { timestamp: '8AM', steps: 150, activeMinutes: 12, gaitSpeed: 0.8, gaitStability: 82 },
    { timestamp: '10AM', steps: 450, activeMinutes: 25, gaitSpeed: 0.75, gaitStability: 80 },
    { timestamp: '12PM', steps: 850, activeMinutes: 45, gaitSpeed: 0.72, gaitStability: 78 },
    { timestamp: '2PM', steps: 1200, activeMinutes: 62, gaitSpeed: 0.7, gaitStability: 75 },
    { timestamp: '4PM', steps: 1500, activeMinutes: 85, gaitSpeed: 0.68, gaitStability: 73 },
    { timestamp: '6PM', steps: 1800, activeMinutes: 95, gaitSpeed: 0.65, gaitStability: 70 },
    { timestamp: '8PM', steps: 2000, activeMinutes: 105, gaitSpeed: 0.63, gaitStability: 68 },
  ];

  const metrics = {
    dailySteps: 2000,
    activeMinutes: 105,
    averageGaitSpeed: 0.7,
    gaitStabilityScore: 75,
    stepsGoal: 3000,
    activeMinutesGoal: 150,
    gaitSpeedThreshold: 0.8,
    gaitStabilityThreshold: 80
  };

  const getStatusColor = (current: number, threshold: number, inverse: boolean = false) => {
    const comparison = inverse ? current < threshold : current >= threshold;
    return comparison ? 'text-green-500' : 'text-yellow-500';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Activity & Mobility Tracking</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Daily Steps</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.dailySteps}</p>
          <p className={`text-sm ${getStatusColor(metrics.dailySteps, metrics.stepsGoal)}`}>
            Goal: {metrics.stepsGoal} steps
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Active Minutes</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.activeMinutes}</p>
          <p className={`text-sm ${getStatusColor(metrics.activeMinutes, metrics.activeMinutesGoal)}`}>
            Goal: {metrics.activeMinutesGoal} minutes
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <TrendingUp className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Gait Speed</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.averageGaitSpeed.toFixed(1)}</p>
          <p className={`text-sm ${getStatusColor(metrics.averageGaitSpeed, metrics.gaitSpeedThreshold)}`}>
            Target: {metrics.gaitSpeedThreshold} m/s
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Gait Stability</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.gaitStabilityScore}</p>
          <p className={`text-sm ${getStatusColor(metrics.gaitStabilityScore, metrics.gaitStabilityThreshold)}`}>
            Target: {metrics.gaitStabilityThreshold}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Daily Activity Trends</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={activityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="steps" 
                stroke="#8884d8" 
                name="Steps"
                strokeWidth={2}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="activeMinutes" 
                stroke="#82ca9d" 
                name="Active Minutes"
                strokeWidth={2}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="gaitStability" 
                stroke="#ffc658" 
                name="Gait Stability %"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Activity Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-yellow-500" />
            </div>
            <div>
              <h4 className="font-medium">Decreased Activity Level</h4>
              <p className="text-gray-600">Daily steps are below target. Consider encouraging more movement during morning hours.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <TrendingUp className="text-green-500" />
            </div>
            <div>
              <h4 className="font-medium">Improved Gait Stability</h4>
              <p className="text-gray-600">Morning gait stability scores show improvement. Continue monitoring for consistency.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Clock className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Peak Activity Times</h4>
              <p className="text-gray-600">Most active between 10 AM and 2 PM. Consider spreading activity throughout the day.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityTracking; 